'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePathname } from 'next/navigation';

import { useNavigate } from '@hooks/useNavigation';
import { useSchoolListing } from '@hooks/useSchoolListing';
import { useSchoolProfile } from '@hooks/useSchoolProfile';
import { useHeaderVisibilityStore } from '@providers/header-visibility-store-provider';
import { Listing } from 'types/collections';

import { Button } from '@components/Button';
import { BottomContent, PageBottom, PageContent, PageGrid } from '@components/Common';
import { LoadingErrorHandler } from '@components/LoadingErrorHandler';

import {
  filterListingUpdate,
  initializeListingData,
  isListingPublished,
  ListingData,
  shouldShowEditMode,
} from '@utils/helpers/schoolListingHelpers';

import { CompletedSchoolListing } from './components/CompletedSchoolListing';
import { EditSchoolListing, EditSchoolListingRef } from './components/EditSchoolListing';

/**
 * School Listing component
 */
export const SchoolListing: React.FC = () => {
  const { t } = useTranslation('common');
  const { goToPath } = useNavigate();
  const pathname = usePathname();
  const setShowBackButton = useHeaderVisibilityStore((state) => state.setShowBackButton);
  const setOnBack = useHeaderVisibilityStore((state) => state.setOnBack);
  const { schoolProfile } = useSchoolProfile();
  const {
    listing,
    isPending: isListingPending,
    updateListing,
    createListing,
    isError,
  } = useSchoolListing();

  const editSchoolListingRef = useRef<EditSchoolListingRef>(null);
  const [listingData, setListingData] = useState<ListingData>(initializeListingData(null));
  const [isEditMode, setIsEditMode] = useState(false);

  // Derive published state from backend data
  const isPublished = isListingPublished(listing || null);
  // Handle back navigation to shifts page
  const handleBack = useCallback(() => {
    const pathSegments = pathname?.split('/').filter(Boolean) || [];
    if (pathSegments.length >= 2) {
      // If listing is published, go to shifts page, otherwise go to new-shift
      const basePath = isPublished 
        ? `/${pathSegments[0]}/shifts` 
        : `/${pathSegments[0]}/shifts/new-shift`;
      goToPath(basePath);
    }
  }, [pathname, goToPath, isPublished]);

  // Set up header back button
  useEffect(() => {
    setShowBackButton(true);
    setOnBack(handleBack);

    // Clean up on unmount
    return () => {
      setShowBackButton(false);
      setOnBack(undefined);
    };
  }, [setShowBackButton, setOnBack, pathname, handleBack]);

  // Generic save handler
  const handleSave = async () => {
    // Validate form first
    const isValid = editSchoolListingRef.current?.validateForm();
    
    if (!isValid) {
      return;
    }

    try {
      if (listing) {
        // Update existing listing
        await updateListing({
          ...listingData,
          listing_completed: true,
        });
      } else {
        // Create new listing first
        await createListing(listingData);
        // Then update it to mark as completed
        await updateListing({
          listing_completed: true,
        });
      }
      setIsEditMode(false);
    } catch (error) {
      throw new Error(`${error}`);
    }
  };

  // Initialize form data when listing loads
  useEffect(() => {
    if (listing) {
      const initialData = initializeListingData(listing);
      setListingData(initialData);

      if (listing.listing_completed) {
        setIsEditMode(false);
      }
    }
  }, [listing]);

  const handleListingChange = (updatedListing: Partial<Listing>) => {
    const filteredUpdate = filterListingUpdate(updatedListing);
    setListingData((prev) => ({ ...prev, ...filteredUpdate }));
  };

  const renderListingContent = () => {
    if (shouldShowEditMode(listing || null, isPublished, isEditMode)) {
      return (
        <EditSchoolListing
          ref={editSchoolListingRef}
          schoolProfile={schoolProfile || null}
          listing={listing}
          onListingChange={handleListingChange}
        />
      );
    } else if (listing && isPublished) {
      return <CompletedSchoolListing schoolProfile={schoolProfile || null} listing={listing} />;
    }
    return null;
  };

  const getBottomButton = () => {
    if (isEditMode) {
      return <Button label={t('publish')} onClick={() => handleSave()} />;
    }

    if (listing && isPublished) {
      return <Button label={t('modify')} onClick={() => setIsEditMode(true)} />;
    }

    return <Button label={t('publish')} onClick={() => handleSave()} />;
  };

  return (
    <PageGrid className="full-height" $marginTopLarge={false}>
      <PageContent>
        <LoadingErrorHandler isLoading={isListingPending} isError={isError}>
          {renderListingContent()}
        </LoadingErrorHandler>
      </PageContent>
      <PageBottom>
        <BottomContent>{getBottomButton()}</BottomContent>
      </PageBottom>
    </PageGrid>
  );
};
